/**
 * Selecting on a phone — list engine v3 §7c.
 *
 * A bar **fixed to the bottom of the screen**, in the place the pager would
 * be, because a phone's bottom edge holds exactly one anchored thing and
 * while you are selecting, this is it. The list scrolls under it — the screen
 * adds `pb-[88px]` while selecting — so the last card is still reachable.
 *
 * It was `sticky bottom-0`, which looks right in a short list and is wrong in
 * every long one: sticky can only move an element *within its own parent's
 * box*, and this is the last child of the page column, so it was already at
 * that box's bottom edge and had nowhere to shift to. The bar sat under the
 * final card, below the fold, and the only way to reach the thing that acts
 * on a selection was to scroll past everything you had just selected.
 * `fixed` is what "anchored to the screen" actually means, and it is what
 * `ScrollTopButton` beside it already uses.
 *
 * `Select all N` sits **under** the count rather than beside it, because at
 * 390px the two of them side by side plus a primary button is three things
 * competing for a row that has 358px of usable width.
 */
import { Button } from './primitives.js'
import { IconCheck, IconClose } from '../frame/icons.js'
import { formatNumber } from '../lib/format.js'
import type { Selection } from '../lib/selection.js'

export interface PhoneSelectionBarProps {
  selection: Selection
  count: number
  /** The filtered number — what "Select all" offers, never the tenant total. */
  visibleCount: number
  onSelectAll: () => void
  onClear: () => void
  onBulk: () => void
  /**
   * Pixels of chrome already owning the bottom edge — a record page's 58px
   * tab bar. The same number `ScrollTopButton` takes, for the same reason.
   */
  bottom?: number
}

export function PhoneSelectionBar({
  selection, count, visibleCount, onSelectAll, onClear, onBulk, bottom = 0,
}: PhoneSelectionBarProps) {
  const matching = selection.mode === 'matching'

  return (
    <div
      data-selection-bar
      style={{ bottom: `${bottom}px` }}
      className="fixed inset-x-0 z-30 flex items-center gap-3 border-t border-line bg-surface px-4 py-2.5 pb-safe"
    >
      <button
        type="button"
        aria-label="Stop selecting"
        onClick={onClear}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-chip bg-hover text-text-muted"
      >
        <IconClose size={18} />
      </button>

      <span className="flex min-w-0 flex-1 flex-col">
        <b className="truncate text-[15px] font-bold text-text">
          {matching
            ? `All ${formatNumber(count)} selected`
            : `${formatNumber(count)} selected`}
        </b>
        {matching
          ? (
            <button
              type="button"
              onClick={onClear}
              className="self-start text-[12.5px] font-semibold text-accent"
            >
              Clear
            </button>
          )
          : visibleCount > count && (
            <button
              type="button"
              onClick={onSelectAll}
              className="self-start text-[12.5px] font-semibold text-accent"
            >
              Select all {formatNumber(visibleCount)}
            </button>
          )}
      </span>

      <Button variant="primary" size="big" onClick={onBulk} className="shrink-0 gap-1.5">
        <IconCheck size={16} />
        Bulk actions
      </Button>
    </div>
  )
}
